import {
  runSessionOp,
  markPinRequired,
  isPinRequired,
  clearPinRequired,
} from "src/utils/employeeSessionOps";
import axios from "src/utils/axios";
import { retireEmployeeSessionToken } from "src/utils/employeeSessionEvents";
import { createSlice } from "@reduxjs/toolkit";

// F4 · Sesión de empleado (PIN → JWT firmado `X-Employee-Session`).
// - Vive en su propio slice para poder persistirlo aparte del auth admin.
// - El interceptor de axios lee `window.localStorage.EmployeeSessionToken`
//   en cada request; aquí sincronizamos localStorage + Redux.

const LS_TOKEN = "EmployeeSessionToken";
const LS_INFO = "EmployeeSessionInfo";
const LS_EXPIRES = "EmployeeSessionExpiresAt";
// Sesión web (usuario|sessionIdentifier) en la que se emitió: distingue una
// sesión viva de un resto de otro login al recargar (employeeSessionRestore).
const LS_OWNER = "EmployeeSessionOwner";

const ownerMarker = () => {
  try {
    const userId = window.localStorage.getItem("userId");
    const sid = window.localStorage.getItem("sessionIdentifier");
    return userId && sid ? `${userId}|${sid}` : "";
  } catch {
    return "";
  }
};

const readInitial = () => {
  try {
    const token = window.localStorage.getItem(LS_TOKEN) || null;
    const info = JSON.parse(window.localStorage.getItem(LS_INFO) || "null");
    const expiresAt = window.localStorage.getItem(LS_EXPIRES) || null;
    if (token && expiresAt && new Date(expiresAt) < new Date()) {
      window.localStorage.removeItem(LS_TOKEN);
      window.localStorage.removeItem(LS_INFO);
      window.localStorage.removeItem(LS_EXPIRES);
      return { token: null, info: null, expiresAt: null };
    }
    return { token, info, expiresAt };
  } catch {
    return { token: null, info: null, expiresAt: null };
  }
};

const bootstrap = readInitial();

const initialState = {
  isLoading: false,
  error: null,
  token: bootstrap.token,
  expiresAt: bootstrap.expiresAt,
  info: bootstrap.info,
  activeSessions: [],
  pinDialog: {
    open: false,
    reason: null,
    action: null,
    onSuccess: null,
    onCancel: null,
    showEmployeeSelector: false,
  },
};

const slice = createSlice({
  name: "employeeSession",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setSession(state, action) {
      const { token, expiresAt, session } = action.payload || {};
      state.isLoading = false;
      state.error = null;
      state.token = token || null;
      state.expiresAt = expiresAt || null;
      state.info = session || null;
      if (token) {
        window.localStorage.setItem(LS_TOKEN, token);
        window.localStorage.setItem(LS_INFO, JSON.stringify(session || null));
        window.localStorage.setItem(LS_EXPIRES, expiresAt || "");
        window.localStorage.setItem(LS_OWNER, ownerMarker());
      }
    },
    clearSession(state) {
      state.token = null;
      state.expiresAt = null;
      state.info = null;
      window.localStorage.removeItem(LS_TOKEN);
      window.localStorage.removeItem(LS_INFO);
      window.localStorage.removeItem(LS_EXPIRES);
      window.localStorage.removeItem(LS_OWNER);
    },
    setActiveSessions(state, action) {
      state.activeSessions = action.payload || [];
    },
    openPinDialog(state, action) {
      state.pinDialog = {
        open: true,
        reason: action.payload?.reason || null,
        action: action.payload?.action || null,
        // Los callbacks NO se serializan pero conviven en el slice mientras
        // el diálogo esté abierto — `configureStore` con
        // `serializableCheck` OFF para este path (ver store.js override).
        onSuccess: action.payload?.onSuccess || null,
        onCancel: action.payload?.onCancel || null,
        // Cuando true, el diálogo fuerza al usuario a ELEGIR primero el
        // empleado (grid con avatares) y luego introducir su PIN. Se
        // usa en el flujo de supervisor override para evitar que el
        // PIN identifique a cualquier empleado que casualmente lo
        // comparta. Default false = comportamiento actual (solo PIN).
        showEmployeeSelector: !!action.payload?.showEmployeeSelector,
      };
    },
    closePinDialog(state) {
      state.pinDialog = {
        open: false,
        reason: null,
        action: null,
        onSuccess: null,
        onCancel: null,
        showEmployeeSelector: false,
      };
    },
  },
});

export const {
  startLoading,
  hasError,
  setSession,
  clearSession,
  setActiveSessions,
  openPinDialog,
  closePinDialog,
} = slice.actions;

export default slice.reducer;

// ── Thunks ────────────────────────────────────────────────────────

// Devuelve:
//   { token, expiresAt, session }         → login OK
//   { code: 'AMBIGUOUS_PIN', candidates } → hay varios empleados con ese PIN
// El caller decide qué hacer con cada caso (pintar selector, etc).
// `employeeId` opcional — cuando el caller sabe qué empleado dice ser
// (kiosk con selección previa, override con supervisor elegido), lo
// pasa y el backend valida SOLO contra ese empleado.
// `preserveOtherSessions` — si true, el login NO revoca sesiones activas
// del mismo device (usado por el supervisor override para no matar la
// sesión del empleado que estaba operando el TPV).
export function loginByPin(pin, deviceId, employeeId = null, preserveOtherSessions = false) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const res = await axios.post(
        "employeePinAuth/loginByPin",
        {
          pin,
          deviceId: deviceId || null,
          employeeId: employeeId ?? null,
          preserveOtherSessions,
        },
        { skipDedupe: true },
      );
      if (res.data?.code === "AMBIGUOUS_PIN") {
        // No es un error — devolvemos el payload tal cual y limpiamos
        // el loading. NO setSession porque aún no hay sesión emitida.
        dispatch(hasError(null));
        return res.data;
      }
      dispatch(setSession(res.data));
      clearPinRequired(res.data?.session?.employeeId);
      return res.data;
    } catch (err) {
      const msg =
        err?.message ||
        err?.code ||
        (typeof err === "string" ? err : "PIN incorrecto");
      dispatch(hasError(msg));
      throw err;
    }
  };
}

// 2026-10-07 · Comprueba el PIN de un empleado SIN identificarlo en el
// dispositivo (no emite sesión, no cambia el empleado de la tablet, no
// expulsa a nadie). Para «Fichar»: fichar no es cambiar de empleado.
// Lanza el error del API si el PIN no es válido.
export function verifyEmployeePin(employeeId, pin, deviceId, purpose = "clocking") {
  return async () => {
    const res = await axios.post(
      "employeePinAuth/verify",
      { employeeId, pin, deviceId: deviceId || null, purpose },
      { skipDedupe: true },
    );
    return res.data;
  };
}

// 2026-08-24 · Refresca el JWT firmado sin pedir PIN. El backend
// valida la sesión actual y emite un nuevo token con las actions
// ACTUALES del rol (tras cambios de rol en tiempo real, o simplemente
// prórroga antes de las 12h de expiración). Se llama:
//   • Desde el interceptor axios cuando quedan <2h para expirar
//     (silent auto-renew — el camarero no se entera).
//   • Manualmente desde el hook role-permissions-changed si se
//     quiere aplicar los nuevos permisos sin re-login.
// Devuelve boolean de éxito para que el caller decida qué hacer
// (mostrar snackbar, fallback a logout, etc.).
// Renovación en una sola vuelo (ver employeeSessionOps): la comparten el
// arranque, el keep-alive y el auto-refresh del interceptor. Rechaza con el
// error del servidor para que el caller distinga "sesión muerta" de "sin red".
export function postSessionRefresh(dispatch) {
  return runSessionOp("refresh", async () => {
    const res = await axios.post(
      "employeePinAuth/refresh",
      {},
      { skipDedupe: true, skipIdempotency: true, __isSessionRefresh: true },
    );
    dispatch(setSession(res.data));
    return res.data;
  });
}

export function refreshEmployeeSession() {
  return async (dispatch) => {
    try {
      await postSessionRefresh(dispatch);
      return true;
    } catch (err) {
      // Silent-fail — el caller decide. Al próximo request el
      // middleware ya bloqueará si el token caducó / se revocó.
      return false;
    }
  };
}

// 2026-08-24 · Emite JWT firmado para un empleado sin PIN. El backend
// solo lo permite si `employee.Password` está vacío — si tiene PIN,
// exige `loginByPin` normal. Usado por el KioskOverlay al identificar
// un empleado que no tiene PIN configurado, para evitar seguir
// mandando `UserEmployeeId` legacy sin firmar en cada request.
//
// 2026-08-25 · Cuando el backend responde 400 con `code=PIN_REQUIRED`
// (device enforcement asignó un empleado que sí tiene PIN configurado),
// tratamos el caso como esperado: NO dispatchamos `hasError`, NO
// re-lanzamos → el caller cae a header legacy sin ensuciar consola ni
// disparar el globalErrorHandler. Devolvemos null para que el caller
// pueda distinguir "sesión emitida" de "sesión NO necesaria".
export function issueEmployeeSessionWithoutPin(employeeId, deviceId) {
  return async (dispatch) => {
    // Ya sabemos que tiene PIN: no se vuelve a preguntar al servidor en
    // cada comprobación (antes: bucle de issueWithoutPin → 400 PIN_REQUIRED).
    if (isPinRequired(employeeId)) return null;
    dispatch(startLoading());
    try {
      const res = await runSessionOp(`issue:${employeeId}`, () => axios.post(
        "employeePinAuth/issueWithoutPin",
        { employeeId, deviceId: deviceId || null },
        { skipDedupe: true },
      ));
      dispatch(setSession(res.data));
      return res.data;
    } catch (err) {
      // axios interceptor rechaza con `error.response.data` directamente
      // (ver utils/axios.js) → el body está en `err` mismo, no en
      // err.response.data.
      const code = err?.code || err?.response?.data?.code;
      if (code === "PIN_REQUIRED") {
        markPinRequired(employeeId);
        return null; // Esperado: el empleado tiene PIN → header legacy
      }
      dispatch(hasError(err?.message || "Error emitiendo sesión"));
      throw err;
    }
  };
}

// 2026-10-18 · Se suelta la sesión EN LOCAL ANTES de avisar al servidor.
// Antes se esperaba a la respuesta del logout para limpiar: durante esa
// ventana el admin (o el siguiente empleado) seguía mandando el token del
// empleado anterior; el servidor, ya revocado, contestaba 401
// EMPLOYEE_SESSION_EXPIRED y el TPV volvía al kiosko → al pasarse al admin
// con PIN se volvía a pedir el PIN. Además, si a la vez se emitía la sesión
// del siguiente empleado (sin PIN), el `clearSession` tardío la borraba.
// El token soltado se marca como «retirado» para que esos 401 tardíos no
// se traten como una sesión caducada, y el logout viaja con ESE token.
export function logoutEmployee() {
  return async (dispatch) => {
    let token = null;
    try { token = window.localStorage.getItem(LS_TOKEN); } catch { /* ignore */ }
    if (token) retireEmployeeSessionToken(token);
    dispatch(clearSession());
    if (!token) return;
    try {
      await axios.post("employeeActiveSession/logout", null, {
        skipDedupe: true,
        skipIdempotency: true,
        __employeeSessionToken: token,
      });
    } catch { /* sesión ya inválida en backend, seguimos */ }
  };
}

// F6 · Vista admin — cargar sesiones activas + revocar.
export function fetchActiveSessions() {
  return async (dispatch) => {
    try {
      const res = await axios.get("employeeActiveSession/active");
      dispatch(setActiveSessions(res.data || []));
    } catch (err) {
      dispatch(hasError(err?.message || "No se pudo cargar sesiones activas"));
    }
  };
}

export function revokeSession(jti, reason) {
  return async (dispatch) => {
    await axios.post(
      `employeeActiveSession/revoke/${jti}?reason=${encodeURIComponent(reason || "ADMIN_REVOKE")}`,
      null,
      { skipDedupe: true, skipIdempotency: true },
    );
    dispatch(fetchActiveSessions());
  };
}

// 2026-09-09 · Cierre NUCLEAR: revoca TODAS las sesiones PIN activas
// del restaurante. Uso restringido a admin. La UI DEBE confirmar en 2
// pasos con motivo obligatorio antes de invocar. Devuelve el número
// de sesiones revocadas para feedback al usuario.
export function revokeAllSessions(reason) {
  return async (dispatch) => {
    const res = await axios.post(
      "employeeActiveSession/revokeAll",
      { reason },
      { skipDedupe: true, skipIdempotency: true },
    );
    dispatch(fetchActiveSessions());
    return res.data?.revoked ?? 0;
  };
}

// ── Helpers puros ─────────────────────────────────────────────────

// Devuelve el token pelado (para el interceptor). Vive fuera del hook
// para poder usarse desde axios.js sin conectar Redux.
export function getEmployeeSessionToken() {
  try {
    const token = window.localStorage.getItem(LS_TOKEN);
    const expiresAt = window.localStorage.getItem(LS_EXPIRES);
    if (!token) return null;
    if (expiresAt && new Date(expiresAt) < new Date()) return null;
    return token;
  } catch {
    return null;
  }
}
